import type { ActivityOf } from '@wyn/content';
import { assertGame } from '../errors.ts';
import { createRng } from '../rng.ts';
import { clamp, type MinigameResult, plural, type TurnEngineImpl } from './types.ts';

export type SiegeMove = { type: 'loose'; /** Counterweight, tonnes, 1–10. */ weight: number; /** Release angle, degrees, 15–75. */ angle: number };

/** Flight is integrated in fixed steps of simulated seconds; every sixth point is kept for drawing. */
const STEP = 2.5 / 360;
const KEEP_EVERY = 6;
const MAX_STEPS = 20_000;
export const SIEGE_WALL_WIDTH = 8;
/** Hits above this fraction of what still stands of the wall smash the battlements for double damage. */
export const SIEGE_UPPER = 0.66;

type Point = [x: number, y: number];

export interface SiegeState {
  wallX: number;
  wind: number;
  hp: number;
  shot: number;
  dmg: number;
  trails: Point[][];
  last: { hit: number; short: boolean; weight: number; angle: number } | null;
  over: boolean;
}

export interface SiegeView {
  wallX: number;
  wallWidth: number;
  wallHeight: number;
  upper: number;
  hp: number;
  maxHp: number;
  /** Wind for the next stone; positive blows towards the wall. */
  wind: number;
  shot: number;
  shots: number;
  /** Every stone's flight so far, oldest first; the last is the stone just loosed. */
  trails: Point[][];
  last: SiegeState['last'];
  over: boolean;
}

/** One stone's flight from the trebuchet, with what it struck. Deterministic. */
export function siegeFlight(a: ActivityOf<'siege'>, s: Pick<SiegeState, 'wallX' | 'wind' | 'hp'>, weight: number, angle: number) {
  const v = 10 + weight * 4.5, th = (angle * Math.PI) / 180, vx = v * Math.cos(th), vy = v * Math.sin(th), wa = s.wind * 0.35;
  const wh = a.wallHeight * Math.max(clamp(s.hp / a.hp, 0, 1), 0.15);
  const path: Point[] = [[0, 0]];
  let px = 0, t = 0;
  for (let i = 1; i <= MAX_STEPS; i++) {
    t += STEP;
    const x = vx * t + 0.5 * wa * t * t, y = vy * t - 4.9 * t * t;
    let out: { hit: number; short: boolean } | null = null;
    if (px < s.wallX && x >= s.wallX && y <= wh && y >= 0) out = { hit: y >= wh * SIEGE_UPPER ? 2 : 1, short: false };
    else if (y < 0) out = { hit: 0, short: x < s.wallX };
    else if (x > 320 || x < -20) out = { hit: 0, short: false };
    if (out || i % KEEP_EVERY === 0) path.push([round(x), round(Math.max(0, y))]);
    if (out) return { path, ...out };
    px = x;
  }
  return { path, hit: 0, short: false };
}
const round = (v: number) => Math.round(v * 100) / 100;

const headwinds = new Map<string, number>();
/**
 * The strongest headwind a contract blows: the most against which a full counterweight still carries
 * a stone to the far end of its range (less a tenth, to spare a crumbling wall). Up to the Keep that
 * is the contract's whole `wind`; further out, an unchecked headwind left some walls out of every
 * stone's reach. Worked out once per contract.
 */
export function siegeHeadwind(a: ActivityOf<'siege'>): number {
  const known = headwinds.get(a.key);
  if (known !== undefined) return known;
  const reaches = (wind: number) => {
    for (let angle = 15; angle <= 75; angle++) if (siegeFlight(a, { wallX: a.distance[1], wind: -wind, hp: a.hp }, 10, angle).hit) return true;
    return false;
  };
  let head = a.wind;
  if (!reaches(a.wind)) {
    let lo = 0, hi = a.wind;
    for (let i = 0; i < 16; i++) {
      const mid = (lo + hi) / 2;
      if (reaches(mid)) lo = mid;
      else hi = mid;
    }
    head = round(lo * 0.9);
  }
  headwinds.set(a.key, head);
  return head;
}

/**
 * The Siege Works: batter a wall with a trebuchet on contract. Set the counterweight and release angle
 * and read the wind; upper-wall hits do double damage. Ported from the Harrowmere prototype.
 */
export const siege: TurnEngineImpl<ActivityOf<'siege'>, SiegeState, SiegeMove, SiegeView> = {
  init(a, _level, serverSeed) {
    const rng = createRng(serverSeed);
    const wallX = a.distance[0] + rng.next() * (a.distance[1] - a.distance[0]);
    const head = siegeHeadwind(a);
    const wind = -head + rng.next() * (head + a.wind);
    return { wallX, wind, hp: a.hp, shot: 0, dmg: 0, trails: [], last: null, over: false };
  },

  move(a, s, m, { rng }) {
    assertGame(!s.over, 'INVALID', 'The contract is done.');
    assertGame(Number.isFinite(m.weight) && m.weight >= 1 && m.weight <= 10, 'INVALID', 'The counterweight runs from 1 to 10 tonnes.');
    assertGame(Number.isInteger(m.angle) && m.angle >= 15 && m.angle <= 75, 'INVALID', 'The release angle runs from 15° to 75°.');
    const weight = Math.round(m.weight * 10) / 10;
    const f = siegeFlight(a, s, weight, m.angle);
    const hp = s.hp - f.hit;
    const shot = s.shot + 1;
    const wind = clamp(s.wind + (rng.next() * 1.2 - 0.6), -siegeHeadwind(a), a.wind);
    return {
      ...s,
      hp,
      dmg: s.dmg + f.hit,
      shot,
      wind,
      trails: [...s.trails, f.path],
      last: { hit: f.hit, short: f.short, weight, angle: m.angle },
      over: hp <= 0 || shot >= a.shots,
    };
  },

  view(a, s) {
    return {
      wallX: s.wallX,
      wallWidth: SIEGE_WALL_WIDTH,
      wallHeight: a.wallHeight,
      upper: SIEGE_UPPER,
      hp: Math.max(0, s.hp),
      maxHp: a.hp,
      wind: s.wind,
      shot: s.shot,
      shots: a.shots,
      trails: s.trails,
      last: s.last,
      over: s.over,
    };
  },

  result(a, s): MinigameResult | null {
    if (!s.over) return null;
    const breach = s.hp <= 0;
    const frac = clamp(s.dmg / a.hp, 0, 1), early = breach ? (a.shots - s.shot) / a.shots : 0;
    const coin = Math.round(a.prize * (breach ? 1 : frac * 0.6));
    return {
      score: breach ? clamp(0.75 + early * 0.5, 0, 1) : frac * 0.7,
      title: breach ? 'The wall is breached!' : 'The wall still stands',
      text: breach ? `You breach it in ${plural(s.shot, 'stone')}. The captain pays ${coin} coin.` : `You do ${s.dmg} of ${a.hp} damage and are paid ${coin} coin.`,
      breakdown: [['Damage', frac], ['Stones to spare', early]],
      yield: null,
      coin,
      xpMult: 1,
      feats: breach && early >= 0.5 ? ['breach-early'] : [],
    };
  },
};
